<template>
  <div class="metrics-chart">
    <h3>Credit Distribution</h3>

    <div v-if="Object.keys(metrics).length === 0" class="no-data">
      <p>No data to display</p>
    </div>

    <div v-else class="chart-content">
      <!-- Bar Chart -->
      <div class="bar-chart">
        <div
          v-for="(metric, key) in filteredMetrics"
          :key="key"
          class="bar-item"
        >
          <div class="bar-container">
            <div
              class="bar"
              :style="{
                height: getBarHeight(metric.count),
                backgroundColor: metric.color,
              }"
              :title="`${metric.label}: ${metric.count}`"
            ></div>
            <span class="bar-value">{{ metric.count }}</span>
          </div>
          <span class="bar-label">{{ metric.label }}</span>
        </div>
      </div>

      <!-- Legend -->
      <div class="chart-legend">
        <div
          v-for="(metric, key) in filteredMetrics"
          :key="`legend-${key}`"
          class="legend-item"
        >
          <span
            class="legend-color"
            :style="{ backgroundColor: metric.color }"
          ></span>
          <span class="legend-label">{{ metric.label }}</span>
          <span class="legend-value">({{ formatCurrency(metric.total) }})</span>
        </div>
      </div>

      <!-- Percentage Breakdown -->
      <div class="percentage-breakdown">
        <h4>Breakdown</h4>
        <div v-for="(metric, key) in filteredMetrics" :key="`percent-${key}`" class="percent-row">
          <span class="label">{{ metric.label }}</span>
          <div class="percent-bar">
            <div
              class="percent-fill"
              :style="{
                width: getPercentage(metric.count) + '%',
                backgroundColor: metric.color,
              }"
            ></div>
          </div>
          <span class="percent-text">{{ getPercentage(metric.count) }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
});

const formatCurrency = (value) => {
  if (!value) return '₦0.00';
  return `₦${(value / 1000).toFixed(1)}K`;
};

const filteredMetrics = computed(() => {
  const result = {};
  ['dueIn30Days', 'dueIn7Days', 'dueIn1Day', 'overdue'].forEach((key) => {
    if (props.metrics[key]) {
      result[key] = props.metrics[key];
    }
  });
  return result;
});

const maxCount = computed(() => {
  const counts = Object.values(filteredMetrics.value).map((m) => m.count || 0);
  return Math.max(...counts, 1);
});

const totalCount = computed(() => {
  return Object.values(filteredMetrics.value).reduce((sum, m) => sum + (m.count || 0), 0);
});

const getBarHeight = (count) => {
  const percentage = (count / maxCount.value) * 100;
  return `${Math.max(percentage, 10)}%`;
};

const getPercentage = (count) => {
  if (totalCount.value === 0) return 0;
  return Math.round((count / totalCount.value) * 100);
};
</script>

<script>
export default {
  name: 'CreditMetricsChart',
};
</script>

<style scoped>
.metrics-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.metrics-chart h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.chart-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Bar Chart */
.bar-chart {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  padding: 20px 10px;
  background: #f9f9f9;
  border-radius: 6px;
  gap: 15px;
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 150px;
  width: 50px;
  justify-content: flex-end;
  gap: 8px;
}

.bar {
  width: 100%;
  min-height: 20px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.bar:hover {
  filter: brightness(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.bar-value {
  font-weight: 700;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.bar-label {
  font-size: 12px;
  color: #666;
  text-align: center;
  max-width: 70px;
  line-height: 1.2;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Legend */
.chart-legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 6px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  font-weight: 600;
  color: #333;
}

.legend-value {
  color: #999;
  margin-left: auto;
}

/* Percentage Breakdown */
.percentage-breakdown {
  padding: 15px;
}

.percentage-breakdown h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.percent-row {
  display: grid;
  grid-template-columns: 100px 1fr 50px;
  align-items: center;
  gap: 15px;
  margin-bottom: 12px;
}

.percent-row:last-child {
  margin-bottom: 0;
}

.label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.percent-bar {
  height: 24px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.percent-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.percent-text {
  text-align: right;
  font-weight: 700;
  color: #333;
  font-size: 13px;
}

@media (max-width: 768px) {
  .bar-chart {
    height: 150px;
  }

  .bar-container {
    height: 120px;
  }

  .chart-legend {
    grid-template-columns: 1fr;
  }

  .percent-row {
    grid-template-columns: 80px 1fr 40px;
    gap: 10px;
  }
}
</style>
